import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  TrendingUp,
  Shield,
  Calculator,
  Users,
  Rocket,
  CheckCircle,
  Mail,
  Award,
  Clock,
  Target,
  Star,
  Phone,
  ExternalLink
} from "lucide-react";
import SEO from "../components/SEO";
import { getLocalBusinessSchema, getOrganizationSchema } from "../utils/schema";
import sonaliPortrait from "../assets/sonali-mitra.jpg";
import "./Home.css";

export const Home = () => {
  const services = [
    {
      icon: <FileText size={32} />,
      title: "Annual Accounts",
      description:
        "Comprehensive year-end accounts preparation and filing for limited companies and sole traders.",
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Tax Planning & Advisory",
      description:
        "Strategic tax planning to minimize liabilities and maximize efficiency for your business.",
    },
    {
      icon: <Shield size={32} />,
      title: "HMRC Specialist Support",
      description:
        "Expert representation and support for HMRC enquiries, investigations, and compliance matters.",
    },
    {
      icon: <Calculator size={32} />,
      title: "Self Assessment Tax Returns",
      description:
        "Accurate and timely personal tax return preparation for individuals and partnerships.",
    },
    {
      icon: <Users size={32} />,
      title: "Payroll & VAT Services",
      description:
        "Complete payroll management and VAT return preparation with full compliance assurance.",
    },
    {
      icon: <Rocket size={32} />,
      title: "Business Start-Up Support",
      description:
        "Comprehensive guidance for new businesses including company formation and setup.",
    },
  ];

  const whyChoose = [
    {
      icon: <Award size={24} />,
      title: "Fully Qualified",
      description: "FCCA & MAAT qualified with professional certification",
    },
    {
      icon: <Clock size={24} />,
      title: "15+ Years Experience",
      description: "Extensive UK practice experience across diverse industries",
    },
    {
      icon: <Target size={24} />,
      title: "Local Expertise",
      description:
        "Deep understanding of Wolverhampton and West Midlands business landscape",
    },
    {
      icon: <CheckCircle size={24} />,
      title: "HMRC Compliant",
      description: "100% compliance with all UK tax and accounting standards",
    },
  ];

  // Combine LocalBusiness and Organization schema
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getLocalBusinessSchema(),
      getOrganizationSchema()
    ]
  };

  return (
    <div className="home-page">
      <SEO
        title="Accountant Wolverhampton | Tax Returns & Company Accounts | Brightson Accounting"
        description="Expert accountant in Wolverhampton. FCCA & MAAT qualified with 15+ years experience. Tax returns, company accounts, HMRC support. Serving small businesses across West Midlands."
        canonical="/"
        keywords="accountant Wolverhampton, tax returns Wolverhampton, company accounts Wolverhampton, small business accountant Wolverhampton, HMRC accountant West Midlands, self assessment Wolverhampton"
        schema={combinedSchema}
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1 className="hero-title">
              Less Tax. Less Stress. No HMRC Trouble.
            </h1>
            <p className="hero-subtitle">
              Expert tax returns and company accounts handled for you — so you can focus on
              growing your business.
            </p>
            <p className="hero-supporting">
              Trusted by small businesses across Wolverhampton and the West Midlands
            </p>
            <div className="hero-cta">
              <Link
                to="/contact"
                className="btn-primary btn-hero-primary"
              >
                <Mail size={20} />
                Get a Free Consultation
              </Link>
              <Link to="/services" className="btn-secondary btn-hero-secondary">
                See How We Can Help
              </Link>
            </div>
            <p className="hero-cta-note">
              📩 Free consultation • No obligation • Response within 24 hours
            </p>
            
            {/* Premium Phone CTA — integrated, no floating box */}
            <div className="hero-phone-cta" data-testid="hero-phone-cta">
              <p className="phone-cta-label">
                <Phone size={16} aria-hidden="true" />
                Speak directly with an accountant
              </p>
              <a
                href="tel:+447362982967"
                className="phone-cta-number"
                data-testid="hero-phone-link"
                aria-label="Call 07362 982967"
              >
                07362 982967
              </a>
              <p className="phone-cta-availability">Available during UK business hours</p>
              <p className="phone-cta-reassurance">
                No call centres &nbsp;·&nbsp; No obligation &nbsp;·&nbsp; Real business advice
              </p>
            </div>
            
            <div className="hero-badges">
              <div className="hero-badge">
                <Award size={20} />
                <span>FCCA Qualified Accountant</span>
              </div>
              <div className="hero-badge">
                <Award size={20} />
                <span>MAAT Qualified</span>
              </div>
              <div className="hero-badge">
                <Clock size={20} />
                <span>15+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="quick-actions-section" style={{ padding: '3rem 0', background: '#f8f9fa' }}>
        <div className="container">
          <div className="section-header text-center" style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '1rem' }}>💡 Not sure where to start?</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
            <Link to="/corporation-tax-calculator" style={{ textDecoration: 'none' }}>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s', cursor: 'pointer' }} className="quick-action-card">
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💰</div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>Reduce my tax</h3>
                <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>Calculate your potential savings</p>
              </div>
            </Link>
            <Link to="/blog/self-assessment-guide-first-time" style={{ textDecoration: 'none' }}>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s', cursor: 'pointer' }} className="quick-action-card">
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>File my self assessment</h3>
                <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>Step-by-step guide</p>
              </div>
            </Link>
            <Link to="/start-a-business-uk" style={{ textDecoration: 'none' }}>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s', cursor: 'pointer' }} className="quick-action-card">
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚀</div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>Start a business</h3>
                <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>Complete setup guide</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Calculator CTA Section */}
      <section className="calculator-cta-section">
        <div className="container">
          <div className="calculator-cta-card">
            <div className="calculator-cta-icon">
              <Calculator size={48} />
            </div>
            <div className="calculator-cta-content">
              <h2>Could You Be Overpaying Corporation Tax?</h2>
              <p>Use our free calculator to estimate how much you could save</p>
              <div className="calculator-cta-features">
                <span>✓ Instant Results</span>
                <span>✓ No Registration</span>
                <span>✓ 100% Free</span>
              </div>
            </div>
            <div className="calculator-cta-button">
              <Link to="/corporation-tax-calculator" className="btn-calculator">
                <Calculator size={20} />
                Calculate My Savings
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Accountant Section */}
      <section className="section meet-accountant-section" data-testid="meet-accountant-section">
        <div className="container">
          <div className="meet-accountant-layout">
            {/* Left: Portrait */}
            <div className="meet-accountant-portrait">
              <img
                src={sonaliPortrait}
                alt="Sonali Mitra, FCCA-qualified accountant at Brightson Accounting"
                className="meet-accountant-image"
                loading="lazy"
                data-testid="meet-accountant-portrait"
              />
              <div className="meet-accountant-caption">
                <span className="caption-name">Sonali Mitra</span>
                <span className="caption-role">FCCA &amp; MAAT Qualified Accountant</span>
              </div>
            </div>

            {/* Right: Content */}
            <div className="meet-accountant-content">
              <span className="meet-accountant-label">Meet Your Accountant</span>
              <h2 className="meet-accountant-heading">
                Practical Accounting Advice From a Qualified UK Accountant
              </h2>
              <div className="meet-accountant-body">
                <p>
                  Hi, I&rsquo;m Sonali Mitra, an FCCA-qualified accountant helping
                  UK businesses, landlords, sole traders, and limited companies
                  reduce tax, stay compliant, and grow with confidence.
                </p>
                <p>
                  With 15+ years supporting Midlands SMEs, I focus on practical,
                  responsive accounting support &mdash; no jargon, no corporate runaround.
                </p>
                <p>
                  At Brightson Accounting, clients work directly with a qualified
                  accountant who genuinely understands small business, HMRC compliance,
                  and tax-efficient growth.
                </p>
              </div>

              {/* Trust badges */}
              <ul className="meet-accountant-trust">
                <li><CheckCircle size={16} aria-hidden="true" /> FCCA Qualified</li>
                <li><CheckCircle size={16} aria-hidden="true" /> MAAT Qualified</li>
                <li><CheckCircle size={16} aria-hidden="true" /> 15+ Years Experience</li>
                <li><CheckCircle size={16} aria-hidden="true" /> Supporting Wolverhampton &amp; West Midlands Businesses</li>
              </ul>

              {/* Achievements card */}
              <div className="meet-accountant-achievements" data-testid="meet-accountant-achievements">
                <h3 className="achievements-title">Recognition &amp; Awards</h3>
                <ul className="achievements-list">
                  <li>
                    <span className="achievement-medal" aria-hidden="true">🏅</span>
                    <span>
                      Awarded the <strong>City &amp; Guilds Medal for Excellence</strong>
                      &nbsp;&mdash; national recognition granted to only 120 students
                      across the UK for outstanding academic achievement.
                    </span>
                  </li>
                  <li>
                    <span className="achievement-medal" aria-hidden="true">🏅</span>
                    <span>
                      Named <strong>City College Coventry&rsquo;s Outstanding
                      Accountancy Student</strong> for 2007&ndash;08.
                    </span>
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="meet-accountant-cta-row">
                <Link
                  to="/contact"
                  className="btn-primary btn-meet-cta"
                  data-testid="meet-accountant-cta"
                >
                  <Mail size={20} aria-hidden="true" />
                  Book a Free Consultation
                </Link>
                <p className="meet-accountant-reassurance">
                  No call centres &nbsp;·&nbsp; Direct accountant access &nbsp;·&nbsp; Response within 24 hours
                </p>
              </div>

              {/* Featured In */}
              <div className="meet-accountant-press" data-testid="meet-accountant-press">
                <span className="press-label">Featured In</span>
                <div className="press-links">
                  <a
                    href="https://www.coventrytelegraph.net/news/local-news/sonalis-amazing-two-years-study-3097019"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="press-link"
                  >
                    Coventry Telegraph
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                  <span className="press-divider" aria-hidden="true">·</span>
                  <a
                    href="https://www.cilex.org.uk/media/media_releases/media_release_archive/legal_students_receive_awards/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="press-link"
                  >
                    CILEX &mdash; Legal Students Receive Awards
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Comprehensive Accounting Services</h2>
            <p className="section-subtitle">
              Expert financial solutions tailored for businesses and individuals
              across Wolverhampton, Dudley, Walsall, and the West Midlands
            </p>
          </div>
          <div className="grid-3">
            {services.map((service, index) => (
              <div key={index} className="card service-card">
                <div className="icon-container">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to="/services" className="service-link">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/contact" className="btn-primary">
              <Mail size={20} />
              Get Started Today
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section section-alt why-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Why Choose Brightson Accounting</h2>
            <p className="section-subtitle">
              Combining professional expertise with personalized service for
              optimal financial outcomes
            </p>
          </div>
          <div className="grid-2">
            {whyChoose.map((item, index) => (
              <div key={index} className="why-card">
                <div className="icon-gold">{item.icon}</div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Expertise Section */}
      <section className="section local-section">
        <div className="container">
          <div className="local-content">
            <div className="local-text">
              <h2>Local Expertise for Wolverhampton Businesses</h2>
              <p>
                Based in the heart of Wolverhampton, we provide specialized
                accounting services to businesses and individuals across the
                West Midlands. Our deep understanding of the local business
                landscape, combined with extensive knowledge of UK tax law,
                enables us to deliver tailored solutions that drive financial
                success.
              </p>
              <p>
                Whether you're a sole trader in Bilston, a limited company in
                Dudley, a landlord in Walsall, or a contractor in Willenhall, we
                offer the expertise and personal attention your finances deserve.
              </p>
              <div className="local-features">
                <div className="local-feature">
                  <CheckCircle size={20} />
                  <span>HMRC Compliance Guaranteed</span>
                </div>
                <div className="local-feature">
                  <CheckCircle size={20} />
                  <span>Fixed Fee Transparency</span>
                </div>
                <div className="local-feature">
                  <CheckCircle size={20} />
                  <span>Same-Day Response Times</span>
                </div>
                <div className="local-feature">
                  <CheckCircle size={20} />
                  <span>Year-Round Support</span>
                </div>
              </div>
            </div>
            <div className="local-stats">
              <div className="stat-card">
                <div className="stat-number">15+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">6</div>
                <div className="stat-label">Core Services</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">100%</div>
                <div className="stat-label">HMRC Compliant</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">24hr</div>
                <div className="stat-label">Email Response</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Financial Management?</h2>
            <p>
              Get in touch with our FCCA-qualified accountant today for expert
              advice tailored to your needs.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="home-cta-email"
            >
              <Mail size={22} />
              Book a Free Consultation
            </Link>
            <p className="cta-note">
              Speak directly with a qualified accountant &mdash; no call centres. Response within 24 hours.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
